/*
 * Product Form Component
 * ---------------------
 * This component handles all cart functionality for the product form, including:
 * - Adding products to cart
 * - Managing special variants
 * - Cart quantity enforcement
 * - Cart drawer updates
 */

// Use window to store these globals and check if they're already defined
window.AUTO_ADD_SPECIAL_VARIANT =
  typeof window.AUTO_ADD_SPECIAL_VARIANT !== 'undefined' ? window.AUTO_ADD_SPECIAL_VARIANT : false; // Set to true to enable automatic addition, false to disable

window.SPECIAL_VARIANT_ID =
  typeof window.SPECIAL_VARIANT_ID !== 'undefined' ? window.SPECIAL_VARIANT_ID : '47343003599085'; // Keep the ID in one place for easy reference

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      /*
       * Constructor initializes:
       * - Form elements and event listeners
       * - Cart notification/drawer references
       * - Submit button states
       * - Error handling preferences
       */
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /*
       * Form Submit Handler
       * ------------------
       * Manages the entire cart addition process:
       * 1. Prevents default form submission
       * 2. Handles loading states
       * 3. Prepares form data
       * 4. Sends cart addition request
       * 5. Handles response and errors
       * 6. Manages special variant 47343003599085
       */
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }

            // Only check for special variant if the feature is enabled
            if (window.AUTO_ADD_SPECIAL_VARIANT) {
              // Check if the specific product is already in the cart
              fetch(`${routes.cart_url}.js`)
                .then((response) => response.json())
                .then((cart) => {
                  const specificProductInCart = cart.items.some(
                    (item) => item.variant_id.toString() === window.SPECIAL_VARIANT_ID
                  );

                  if (!specificProductInCart) {
                    this.submitButton.setAttribute('aria-disabled', 'true');
                    this.submitButton.classList.add('loading');

                    const specificProductQuantity = 1;
                    const specificProductBody = new FormData();
                    specificProductBody.append('id', window.SPECIAL_VARIANT_ID);
                    specificProductBody.append('quantity', specificProductQuantity);
                    if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
                      specificProductBody.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                      );
                      specificProductBody.append('sections_url', window.location.pathname);
                    }
                    const specificProductConfig = { ...config, body: specificProductBody };

                    return fetch(`${routes.cart_add_url}`, specificProductConfig)
                      .then((response) => response.json())
                      .then((response) => {
                        if (!response.status) {
                          publish(PUB_SUB_EVENTS.cartUpdate, {
                            source: 'product-form',
                            productVariantId: window.SPECIAL_VARIANT_ID,
                          });
                        }
                      })
                      .catch((error) => {
                        console.error('Error adding product to cart:', error);
                      })
                      .finally(() => {
                        this.submitButton.classList.remove('loading');
                        this.submitButton.removeAttribute('aria-disabled');
                      });
                  }
                });
            }

            reorderCartItems();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      /*
       * Error Message Handler
       * -------------------
       * Manages the display of error messages:
       * - Shows/hides error message wrapper
       * - Updates error message content
       * - Respects hideErrors setting
       */
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      addSpecificProductIfNeeded() {
        const specificVariantId = '47343003599085';

        // Check if the specific product is already in the cart
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            const specificProductInCart = cart.items.some((item) => item.variant_id.toString() === specificVariantId);

            if (!specificProductInCart) {
              // If the specific product is not in the cart, add it
              const specificProductBody = new FormData();
              specificProductBody.append('id', specificVariantId);
              specificProductBody.append('quantity', 1);

              const config = fetchConfig('javascript');
              config.headers['X-Requested-With'] = 'XMLHttpRequest';
              delete config.headers['Content-Type'];
              config.body = specificProductBody;

              return fetch(`${routes.cart_add_url}`, config)
                .then((response) => response.json())
                .then((response) => {
                  if (!response.status) {
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                      source: 'product-form',
                      productVariantId: specificVariantId,
                      errors: response.errors || response.description,
                      message: response.message,
                    });
                  }
                  // Refresh the cart contents
                  if (this.cart) {
                    this.cart.renderContents(response);
                  }
                })
                .catch((error) => {
                  console.error('Error adding specific product to cart:', error);
                });
            }
          })
          .catch((error) => {
            console.error('Error fetching cart:', error);
          });
      }
    }
  );
}

/*
 * Special Variant Management
 * ------------------------
 * These functions manage the behavior of variant 47343003599085:
 * - Never allows it to be alone in cart
 * - Prevents multiple quantities
 * - Handles automatic removal when needed
 */

/* Flag to prevent multiple simultaneous additions of discount product */
if (typeof isAddingDiscount1Product === 'undefined') {
  var isAddingDiscount1Product = false;
}

/*
 * Cart Drawer Observer
 * ------------------
 * Watches cart drawer for changes:
 * - Monitors open/close states
 * - Triggers variant checks
 * - Maintains cart item order
 */
function observeCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');

  if (cartDrawer) {
    const observer = new MutationObserver(() => {
      if (cartDrawer.classList.contains('active')) {
        checkAndRemoveSpecificVariant(() => {
          reorderCartItems();
        });
      }
    });

    observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }
}

/*
 * Specific Variant Controller
 * -------------------------
 * Ensures variant 47343003599085:
 * 1. Is never alone in cart
 * 2. Cannot have quantity > 1
 * 3. Is removed when conditions aren't met
 */
function checkAndRemoveSpecificVariant(callback) {
  if (!window.AUTO_ADD_SPECIAL_VARIANT) return callback?.();

  const specificVariantId = '47343003599085';

  fetch(`${routes.cart_url}.js`)
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then((cart) => {
      // Check if cart has only the specific variant
      const hasOnlySpecificVariant =
        cart.items.length === 1 && cart.items[0].variant_id.toString() === specificVariantId;

      if (hasOnlySpecificVariant) {
        // Remove the specific variant immediately using the proper Shopify endpoint
        const formData = new FormData();
        formData.append('id', specificVariantId);
        formData.append('quantity', 0);

        return fetch(routes.cart_change_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
      }
    })
    .then((response) => {
      if (response) return response.json();
    })
    .then((updatedCart) => {
      if (updatedCart) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents(updatedCart);
        }
      }
      if (callback) callback();
    })
    .catch((error) => {
      console.error('Error in checkAndRemoveSpecificVariant:', error);
      if (callback) callback();
    });
}

/*
 * Cart Monitoring
 * -------------
 * Regular interval checks to maintain cart rules:
 * - Runs every 2 seconds
 * - Cleans up on page unload
 */
window.CHECK_INTERVAL = typeof window.CHECK_INTERVAL !== 'undefined' ? window.CHECK_INTERVAL : 2000;

// Clear existing interval if it exists
if (window.cartCheck) {
  clearInterval(window.cartCheck);
}

// Only poll the cart when the auto-add feature is enabled — otherwise the
// interval just wakes the main thread every 2s for nothing.
if (window.AUTO_ADD_SPECIAL_VARIANT) {
  window.cartCheck = setInterval(checkAndRemoveSpecificVariant, window.CHECK_INTERVAL);
}

/*
 * Cart Layout Management
 * --------------------
 * Handles visual arrangement of cart items:
 * - Maintains consistent order
 * - Places totals before quantity
 */
function reorderCartItems() {
  const cartItems = document.querySelectorAll('.cart-item');
  let cartItemCount = 0;
  cartItems.forEach((item) => {
    const totalsElement = item.querySelector('.cart-item__totals');
    const quantityElement = item.querySelector('.cart-item__quantity');
    const quantityPerItem = parseInt(quantityElement.querySelector('.quantity__input').value);
    cartItemCount += quantityPerItem;
    if (totalsElement && quantityElement && quantityElement.nextSibling !== totalsElement) {
      item.insertBefore(totalsElement, quantityElement);
    }
  });

  document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
    const countBubble = document.createElement('div');
    countBubble.classList.add('cart-count-bubble');
    countBubble.innerHTML = `<span aria-hidden="true">${cartItemCount}</span><span class="visually-hidden">${cartItemCount} items</span>`;
    bubble.replaceWith(countBubble);
  });
}

/*
 * Discount Product Addition
 * ----------------------
 * Controls addition of variant 47343003599085:
 * - Prevents duplicate additions
 * - Manages loading states
 * - Handles errors
 */
function addDiscount1Product() {
  if (isAddingDiscount1Product) return; // Prevent multiple calls

  isAddingDiscount1Product = true; // Set flag

  const specificVariantId = '47343003599085';
  const specificProductQuantity = 1;
  const specificProductBody = new FormData();
  specificProductBody.append('id', specificVariantId);
  specificProductBody.append('quantity', specificProductQuantity);

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];
  config.body = specificProductBody;

  fetch(`${routes.cart_add_url}`, config)
    .then((response) => response.json())
    .then((response) => {
      if (!response.status) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: specificVariantId,
          errors: response.errors || response.description,
          message: response.message,
        });
      }
    })
    .catch((error) => {
      console.error('Error adding product to cart:', error);
    })
    .finally(() => {
      const submitButton = document.querySelector('product-form').querySelector('[type="submit"]');
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-disabled');
      isAddingDiscount1Product = false; // Reset flag
    });
}

/*
 * Cart Item Pricing Display
 * ----------------------
 * Updates price display for special variant:
 * - Shows regular price
 * - Displays discounted price
 * - Maintains accessibility
 */
function updateCartItemPricing() {
  const cartItems = document.querySelectorAll('.cart-item');
  const specificVariantId = '47343003599085';

  cartItems.forEach((item) => {
    const variantId = item.classList.contains(`cart-item-${specificVariantId}`);
    const priceElement = item.querySelector('.cart-item__details .product-option');

    if (variantId && priceElement) {
      const regularPrice = '$95.00';
      const discountedPriceHtml = `
        <div class="cart-item__discounted-prices">
          <span class="visually-hidden">Regular price</span>
          <s class="cart-item__old-price product-option">${regularPrice}</s>
          <span class="visually-hidden">Sale price</span>
          <strong class="cart-item__final-price product-option">
            ${priceElement.textContent}
          </strong>
        </div>
      `;
      priceElement.outerHTML = discountedPriceHtml;
    }
  });
}

/*
 * Quantity Management
 * -----------------
 * Functions to check and enforce quantity rules:
 * - Prevents multiple quantities
 * - Monitors cart changes
 * - Updates quantities automatically
 */
function checkSpecificVariantQuantity() {
  const specificVariantId = '47343003599085';

  return fetch(`${routes.cart_url}.js`)
    .then((response) => response.json())
    .then((cart) => {
      const specificItem = cart.items.find((item) => item.variant_id.toString() === specificVariantId);
      return specificItem ? specificItem.quantity : 0;
    })
    .catch((error) => {
      console.error('Error checking variant quantity:', error);
      return 0;
    });
}

function enforceSpecificVariantQuantity() {
  if (!window.AUTO_ADD_SPECIAL_VARIANT) return;

  const specificVariantId = '47343003599085';

  checkSpecificVariantQuantity().then((quantity) => {
    if (quantity > 1) {
      // If quantity is more than 1, set it back to 1
      const formData = new FormData();
      formData.append('id', specificVariantId);
      formData.append('quantity', 1);

      fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((cart) => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.renderContents) {
            cartDrawer.renderContents(cart);
          }
        })
        .catch((error) => console.error('Error updating quantity:', error));
    }
  });
}

/*
 * Event Listeners and Observers
 * --------------------------
 * Comprehensive event monitoring:
 * - Page load initialization
 * - Cart updates
 * - Quantity changes
 * - DOM mutations
 */
document.addEventListener('DOMContentLoaded', () => {
  observeCartDrawer();
  checkAndRemoveSpecificVariant(); // Add this line to check on page load
});
